import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatNumber, formatDecimal } from '@/components/sizing/advancedInsightsShared';
import { ArrowRight, Hash, Users, Cpu, Database, Target, HardDrive, Zap, AlertTriangle, Info, Clock } from 'lucide-react';
import type { SizingNarrativeData } from '@/types/narrative';
import { DutyCycleChart } from '@/components/outputs/DutyCycleChart';

type MethodsFormulasTabProps = {
  data: SizingNarrativeData;
};

export const MethodsFormulasTab: React.FC<MethodsFormulasTabProps> = ({ data }) => {
  const { personas, throughput, kv, finalPlan, workload, requests, gpuMemory, personaKvBreakdowns, kvComparison, personaTimings, mcExplanation } = data;

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            This walkthrough explains the complete sizing methodology from persona definition to final GPU count.
            <span className="font-medium"> Personas drive everything</span>: token budgets, duty cycles, throughput requirements, and KV memory usage.
          </p>
        </CardContent>
      </Card>

      {/* Step 1: Persona Definition */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="text-base px-3 py-1">Step 1</Badge>
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Persona Definition</CardTitle>
          </div>
          <CardDescription>Define user segments with unique usage patterns, token requirements, and duty cycles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm space-y-2">
            <div className="text-muted-foreground">For each persona p:</div>
            <div className="mt-2">population = totalUsers × (sharePercent / 100)</div>
            <div>turnsKept = p.turnsKept &gt; 0 ? p.turnsKept : 1</div>
            <div className="mt-2 text-muted-foreground">Per-turn token calculation:</div>
            <div>promptTokensPerTurn = inputTokens / turnsKept</div>
            <div>decodeTokensPerTurn = outputTokens / turnsKept</div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {personas.map((persona) => {
              const timing = personaTimings?.find(t => t.id === persona.id);
              return (
                <div key={persona.id} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{persona.label}</p>
                    <Badge variant="outline">{formatDecimal(persona.sharePct, 0)}%</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div className="flex justify-between">
                      <span>Population:</span>
                      <span className="font-medium text-foreground">{formatNumber(persona.population)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Input Tokens:</span>
                      <span className="font-medium text-foreground">{formatNumber(persona.inputTokensPerConversation)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Output Tokens:</span>
                      <span className="font-medium text-foreground">{formatNumber(persona.outputTokensPerConversation)}</span>
                    </div>
                    {timing && (
                      <>
                        <div className="border-t border-border mt-1 pt-1 flex justify-between">
                          <span>Read Time:</span>
                          <span className="font-medium text-foreground">{formatDecimal(timing.readSeconds, 1)}s</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Think Time:</span>
                          <span className="font-medium text-foreground">{formatDecimal(timing.thinkSeconds, 1)}s</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Service Time:</span>
                          <span className="font-medium text-foreground">{formatDecimal(timing.serviceTimeSec, 1)}s</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Monte Carlo Distribution */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="text-base px-3 py-1">Step 2</Badge>
            <Hash className="h-5 w-5 text-primary" />
            <CardTitle>Monte Carlo Distribution & Duty Cycle</CardTitle>
          </div>
          <CardDescription>Model stochastic user arrivals and compute active session percentiles based on duty cycles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* MC Explanation Callout */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-blue-700 dark:text-blue-400">Understanding "Active" Sessions</p>
                <p className="text-muted-foreground">
                  Users spend significant time in <span className="text-blue-600 font-medium">reading</span> and{' '}
                  <span className="text-amber-600 font-medium">thinking</span> phases. During these phases:
                </p>
                <ul className="list-disc list-inside text-muted-foreground ml-2 space-y-1">
                  <li>They are <span className="font-medium text-foreground">NOT generating tokens</span> (no throughput demand)</li>
                  <li>But their <span className="font-medium text-foreground">KV cache REMAINS resident</span> in GPU memory</li>
                </ul>
                <p className="text-muted-foreground">
                  This is why <span className="font-semibold">KV-resident sessions &gt; throughput-active sessions</span>.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm space-y-2">
            <div className="text-muted-foreground">Duty cycle calculation:</div>
            <div>cycleSec = readTime + thinkTime + serviceTime</div>
            <div>activeRatio = serviceTime / cycleSec</div>
            <div className="mt-3">λ (arrival rate) = targetConcurrent / cycleSec</div>
            <div className="mt-2 text-muted-foreground text-xs">
              Poisson arrivals with rate λ simulate realistic user traffic patterns
            </div>
          </div>

          {/* Duty Cycle Visualization */}
          {personaTimings && personaTimings.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">Persona Duty Cycles</h4>
              </div>
              <div className="bg-muted/30 p-4 rounded-lg">
                <DutyCycleChart personaTimings={personaTimings} />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">P50 Active (Generating)</p>
                <Badge variant="outline">Median</Badge>
              </div>
              <p className="text-3xl font-bold">{formatNumber(finalPlan.mcActiveP50)}</p>
              <p className="text-xs text-muted-foreground mt-1">50% of the time, this many sessions are generating</p>
            </div>
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">P95 Active (Generating)</p>
                <Badge>Sizing Target</Badge>
              </div>
              <p className="text-3xl font-bold text-primary">{formatNumber(finalPlan.mcActiveP95)}</p>
              <p className="text-xs text-muted-foreground mt-1">Used for throughput sizing (95% coverage)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Throughput Calculation */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="text-base px-3 py-1">Step 3</Badge>
            <Cpu className="h-5 w-5 text-primary" />
            <CardTitle>Throughput Calculation</CardTitle>
          </div>
          <CardDescription>Calculate per-session TPS and aggregate P95 throughput requirements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Per-session TPS formulas */}
          <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm space-y-3">
            <div className="text-muted-foreground">Per-session prompt TPS:</div>
            <div>prefillSec = max(ttftMs / 1000, 0.1)</div>
            <div>perSessionPromptTps = promptTokensPerTurn / prefillSec</div>
            
            <div className="mt-4 text-muted-foreground">Per-session decode TPS:</div>
            <div>perSessionDecodeTps = persona.decodeTps</div>
            
            <div className="mt-4 text-muted-foreground">P95 aggregate throughput:</div>
            <div>requiredPromptTps = Σ(p95Active_p × perSessionPromptTps_p)</div>
            <div>requiredDecodeTps = Σ(p95Active_p × perSessionDecodeTps_p)</div>
          </div>

          {/* Per-persona TPS breakdown */}
          {personaTimings && personaTimings.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Per-Persona TPS Breakdown</h4>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Persona</th>
                      <th className="px-3 py-2 text-right font-medium">TTFT (ms)</th>
                      <th className="px-3 py-2 text-right font-medium">Prompt TPS/Sess</th>
                      <th className="px-3 py-2 text-right font-medium">Decode TPS/Sess</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personaTimings.map((timing, idx) => (
                      <tr key={timing.id} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium">{timing.name}</td>
                        <td className="px-3 py-2 text-right">{formatNumber(timing.ttftMs)}</td>
                        <td className="px-3 py-2 text-right">{formatDecimal(timing.perSessionPromptTps, 1)}</td>
                        <td className="px-3 py-2 text-right">{formatDecimal(timing.perSessionDecodeTps, 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-2">Prompt Throughput Requirement</p>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold">{formatNumber(throughput.inputTpsRequired)}</p>
                <p className="text-sm text-muted-foreground">tokens/sec</p>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {formatNumber(throughput.inputTpsPerGpu)} TPS/GPU → 
                <span className="font-medium text-foreground ml-1">
                  {Math.ceil(throughput.gpusForInput)} GPUs needed
                </span>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-2">Decode Throughput Requirement</p>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold">{formatNumber(throughput.outputTpsRequired)}</p>
                <p className="text-sm text-muted-foreground">tokens/sec</p>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {formatNumber(throughput.outputTpsPerGpu)} TPS/GPU → 
                <span className="font-medium text-foreground ml-1">
                  {Math.ceil(throughput.gpusForOutput)} GPUs needed
                </span>
              </div>
            </div>
          </div>
          
          {/* GPU calculation */}
          <div className="bg-muted/50 p-3 rounded font-mono text-xs">
            <div>promptGpus = ceil({formatNumber(throughput.inputTpsRequired)} / {formatNumber(throughput.inputTpsPerGpu)}) = {Math.ceil(throughput.gpusForInput)}</div>
            <div>decodeGpus = ceil({formatNumber(throughput.outputTpsRequired)} / {formatNumber(throughput.outputTpsPerGpu)}) = {Math.ceil(throughput.gpusForOutput)}</div>
          </div>
        </CardContent>
      </Card>

      {/* Step 4: KV Memory Calculation - EXTENDED */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="text-base px-3 py-1">Step 4</Badge>
            <Database className="h-5 w-5 text-primary" />
            <CardTitle>KV Memory Calculation</CardTitle>
          </div>
          <CardDescription>Compute GPU memory requirements for key-value cache retention</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* 4.0: KV Memory Per Token */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold">4.0 KV Memory Per Token</h4>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg space-y-3">
              <p className="text-sm text-muted-foreground">
                Each token in a conversation requires KV cache storage. The memory per token depends on the model architecture:
              </p>

              <div className="bg-muted/50 p-3 rounded font-mono text-xs space-y-1">
                <div className="text-muted-foreground">Formula (per layer, per token):</div>
                <div>kvBytesPerToken = 2 × numLayers × (hiddenSize / numHeads) × numKvHeads × bytesPerElement</div>
                <div className="mt-2 text-muted-foreground">Where:</div>
                <div className="ml-2">• 2 = Key + Value tensors</div>
                <div className="ml-2">• numLayers = transformer layers in model</div>
                <div className="ml-2">• hiddenSize / numHeads = head dimension</div>
                <div className="ml-2">• numKvHeads = KV heads (may differ from attention heads in GQA/MQA)</div>
                <div className="ml-2">• bytesPerElement = 2 (FP16/BF16) or 1 (FP8)</div>
              </div>

              <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Current Model KV Memory:</span>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{formatNumber(kv.kvBytesPerToken)}</p>
                    <p className="text-xs text-muted-foreground">bytes per token</p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-primary/20 text-xs text-muted-foreground">
                  = {formatDecimal(kv.kvBytesPerToken / 1024, 2)} KiB/token 
                  = {formatDecimal(kv.kvBytesPerToken / (1024 * 1024), 4)} MiB/token
                </div>
              </div>
            </div>
          </div>

          {/* 4.1: GPU Memory Budget */}
          {gpuMemory && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">4.1 GPU Memory Budget</h4>
              </div>
              
              <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Total HBM per GPU:</span>
                  <span className="font-bold">{formatDecimal(gpuMemory.hbmGiBPerGpu, 0)} GiB</span>
                </div>
                
                {/* Visual memory bar */}
                <div className="relative h-8 bg-muted rounded-lg overflow-hidden flex">
                  <div 
                    className="bg-orange-500 flex items-center justify-center text-[10px] text-white font-medium"
                    style={{ width: `${(gpuMemory.weightsGiBPerGpu / gpuMemory.hbmGiBPerGpu) * 100}%` }}
                  >
                    {formatDecimal(gpuMemory.weightsGiBPerGpu, 1)} GiB
                  </div>
                  <div 
                    className="bg-yellow-500 flex items-center justify-center text-[10px] text-white font-medium"
                    style={{ width: `${(gpuMemory.runtimeOverheadGiB / gpuMemory.hbmGiBPerGpu) * 100}%` }}
                  >
                    {formatDecimal(gpuMemory.runtimeOverheadGiB, 1)} GiB
                  </div>
                  <div 
                    className="bg-primary flex items-center justify-center text-[10px] text-primary-foreground font-medium"
                    style={{ width: `${(gpuMemory.usableKvGiBPerGpu / gpuMemory.hbmGiBPerGpu) * 100}%` }}
                  >
                    {formatDecimal(gpuMemory.usableKvGiBPerGpu, 1)} GiB KV
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-orange-500 rounded" />
                    <span className="text-muted-foreground">Weights</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded" />
                    <span className="text-muted-foreground">Runtime</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-primary rounded" />
                    <span className="text-muted-foreground">KV Available</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 4.2: GPU Bridging / Tensor Parallelism */}
          {gpuMemory && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">4.2 GPU Bridging (Tensor Parallelism)</h4>
              </div>

              <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                <p className="text-sm text-muted-foreground">
                  GPU bridging (Tensor Parallelism) allows model weights to be distributed across multiple GPUs.
                  With TP={gpuMemory.bridgeGroupSize}, each GPU only stores 1/{gpuMemory.bridgeGroupSize}th of the weights,
                  freeing memory for user KV cache.
                </p>

                <div className="grid md:grid-cols-4 gap-3">
                  <div className="border border-border rounded p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Current TP</p>
                    <p className="text-xl font-bold">{gpuMemory.bridgeGroupSize}</p>
                  </div>
                  <div className="border border-border rounded p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Weights (TP={gpuMemory.bridgeGroupSize})</p>
                    <p className="text-xl font-bold">{formatDecimal(gpuMemory.weightsGiBPerGpu, 1)} GiB</p>
                  </div>
                  <div className="border border-border rounded p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Weights (TP=1)</p>
                    <p className="text-xl font-bold">{formatDecimal(gpuMemory.weightsGiBUnshared, 1)} GiB</p>
                  </div>
                  <div className="border border-primary rounded p-3 text-center bg-primary/5">
                    <p className="text-xs text-muted-foreground mb-1">Memory Recaptured</p>
                    <p className="text-xl font-bold text-primary">{formatDecimal(gpuMemory.memoryRecapturedGiB, 1)} GiB</p>
                  </div>
                </div>

                <div className="bg-muted/50 p-3 rounded font-mono text-xs">
                  <div>usableKvPerGpu = hbmPerGpu - (weightsTotal / TP) - runtimeOverhead</div>
                  <div className="mt-1 text-muted-foreground">
                    = {formatDecimal(gpuMemory.hbmGiBPerGpu, 0)} - {formatDecimal(gpuMemory.weightsGiBPerGpu, 1)} - {formatDecimal(gpuMemory.runtimeOverheadGiB, 1)} = {formatDecimal(gpuMemory.usableKvGiBPerGpu, 1)} GiB
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 4.3: Per-Persona KV Retention */}
          {personaKvBreakdowns && personaKvBreakdowns.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">4.3 Per-Persona KV Retention</h4>
              </div>

              <div className="bg-muted/50 p-3 rounded font-mono text-xs mb-3">
                <div>tokensPerSession = promptTokens + decodeTokens</div>
                <div>kvGiBPerSession = tokensPerSession × kvBytesPerToken / (1024³)</div>
                <div>totalKvGiB_p = p95Sessions_p × kvGiBPerSession</div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                {personaKvBreakdowns.map((breakdown) => (
                  <div key={breakdown.personaId} className="border border-border rounded-lg p-3 space-y-2">
                    <p className="font-medium text-sm">{breakdown.personaName}</p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Sessions P95:</span>
                        <span className="font-medium">{formatDecimal(breakdown.sessionsP95, 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tokens/Session:</span>
                        <span className="font-medium">{formatNumber(breakdown.tokensPerSession)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">KV GiB/Session:</span>
                        <span className="font-medium">{formatDecimal(breakdown.kvGiBPerSession, 3)}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-border">
                        <span className="text-muted-foreground">Total KV:</span>
                        <span className="font-bold">{formatDecimal(breakdown.totalKvGiB, 1)} GiB</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 4.4: Bin Packing Overhead - FIXED */}
          {kvComparison && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">4.4 Bin Packing Overhead</h4>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-700 dark:text-amber-400">Bin Packing is Overhead, Not Efficiency</p>
                    <p className="text-muted-foreground mt-1">
                      Real sessions don't pack perfectly onto GPUs. The First-Fit Decreasing algorithm 
                      accounts for memory fragmentation—this <span className="font-semibold">increases</span> GPU requirements 
                      beyond the theoretical minimum.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                <div className="bg-muted/50 p-3 rounded font-mono text-xs">
                  <div>Theoretical KV GPUs = ceil(totalKvGiB / usableKvGiBPerGpu)</div>
                  <div>Bin-Packed KV GPUs = FFD algorithm result</div>
                  <div className="text-amber-600">Fragmentation Overhead = binPacked − theoretical (always ≥ 0)</div>
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                  <div className="border border-emerald-500/50 rounded p-4 text-center bg-emerald-500/5">
                    <p className="text-xs text-muted-foreground mb-1">Theoretical GPUs</p>
                    <p className="text-2xl font-bold text-emerald-600">{kvComparison.theoreticalKvGpus}</p>
                    <p className="text-xs text-muted-foreground mt-1">Perfect packing (lower bound)</p>
                  </div>
                  <div className="border-2 border-primary rounded p-4 text-center bg-primary/5">
                    <p className="text-xs text-muted-foreground mb-1">Bin-Packed GPUs</p>
                    <p className="text-2xl font-bold text-primary">{kvComparison.binPackedKvGpus}</p>
                    <p className="text-xs text-muted-foreground mt-1">Actual requirement</p>
                  </div>
                  <div className="border border-amber-500/50 rounded p-4 text-center bg-amber-500/5">
                    <p className="text-xs text-muted-foreground mb-1">Fragmentation Overhead</p>
                    <p className="text-2xl font-bold text-amber-600">
                      +{kvComparison.overheadGpus}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Additional GPUs needed</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 4.5: Final KV Memory Sizing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold">4.5 Final KV Memory Sizing</h4>
            </div>

            <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">KV GPUs Required (P95)</span>
                <Badge variant="default" className="text-lg px-3 py-1">{Math.ceil(kv.gpusKv)} GPUs</Badge>
              </div>
              <div className="flex justify-between text-xs mt-3 pt-3 border-t border-primary/20">
                <span className="text-muted-foreground">Total KV Memory:</span>
                <span className="font-medium">{formatDecimal(kv.kvGiBTotal, 1)} GiB</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-muted-foreground">KV Sessions P95:</span>
                <span className="font-medium">{formatNumber(kv.kvSessionsP95)}</span>
              </div>
            </div>

            {/* What-if without bridging */}
            {kvComparison && kvComparison.gpusIfNoBridging < 999 && (
              <div className="border border-destructive/50 rounded-lg p-4 bg-destructive/5">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-destructive">Without GPU Bridging (TP=1)</p>
                    <p className="text-xs text-muted-foreground">
                      If tensor parallelism were not available (TP=1), each GPU would need to hold the full model
                      weights ({formatDecimal(gpuMemory?.weightsGiBUnshared ?? 0, 1)} GiB), leaving only{' '}
                      {formatDecimal((gpuMemory?.hbmGiBPerGpu ?? 0) - (gpuMemory?.weightsGiBUnshared ?? 0) - (gpuMemory?.runtimeOverheadGiB ?? 0), 1)} GiB
                      for KV cache. This would require <span className="font-bold text-destructive">{kvComparison.gpusIfNoBridging} GPUs</span> instead
                      of {Math.ceil(kv.gpusKv)}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {kvComparison && kvComparison.gpusIfNoBridging >= 999 && (
              <div className="border border-destructive rounded-lg p-4 bg-destructive/10">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-destructive">Model Would Not Fit Without Bridging</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      At TP=1, the model weights ({formatDecimal(gpuMemory?.weightsGiBUnshared ?? 0, 1)} GiB) exceed
                      available HBM per GPU ({formatDecimal(gpuMemory?.hbmGiBPerGpu ?? 0, 0)} GiB). Tensor parallelism is
                      essential for this configuration.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

        </CardContent>
      </Card>

      {/* Step 5: Bottleneck Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="text-base px-3 py-1">Step 5</Badge>
            <Target className="h-5 w-5 text-primary" />
            <CardTitle>Bottleneck Analysis</CardTitle>
          </div>
          <CardDescription>Identify the limiting constraint that determines final GPU count</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm">
            <div>finalGPUs = max(promptGPUs, decodeGPUs, kvGPUs, weightsGPUs)</div>
            <div className="mt-2 text-muted-foreground text-xs">
              The constraint requiring the most GPUs becomes the bottleneck
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className={`border rounded-lg p-3 text-center ${
              Math.ceil(throughput.gpusForInput) >= finalPlan.finalGpuCount ? 'border-destructive bg-destructive/5' : 'border-border'
            }`}>
              <p className="text-xs text-muted-foreground mb-1">Prompt</p>
              <p className="text-lg font-bold">{Math.ceil(throughput.gpusForInput)}</p>
            </div>
            <div className={`border rounded-lg p-3 text-center ${
              Math.ceil(throughput.gpusForOutput) >= finalPlan.finalGpuCount ? 'border-destructive bg-destructive/5' : 'border-border'
            }`}>
              <p className="text-xs text-muted-foreground mb-1">Decode</p>
              <p className="text-lg font-bold">{Math.ceil(throughput.gpusForOutput)}</p>
            </div>
            <div className={`border rounded-lg p-3 text-center ${
              Math.ceil(kv.gpusKv) >= finalPlan.finalGpuCount ? 'border-destructive bg-destructive/5' : 'border-border'
            }`}>
              <p className="text-xs text-muted-foreground mb-1">KV Memory</p>
              <p className="text-lg font-bold">{Math.ceil(kv.gpusKv)}</p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm">
            <span className="text-muted-foreground">Limiting Constraint:</span>
            <Badge variant="destructive" className="text-base">
              {finalPlan.limitingConstraint === 'throughput' ? 'Throughput' : 
               finalPlan.limitingConstraint === 'memory' ? 'KV Memory' : 'Model Weights'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Step 6: Final GPU Sizing */}
      <Card className="border-primary">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="text-base px-3 py-1">Step 6</Badge>
            <ArrowRight className="h-5 w-5 text-primary" />
            <CardTitle>Final GPU Sizing</CardTitle>
          </div>
          <CardDescription>Convert GPU count to server deployment and capacity metrics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm space-y-2">
            <div>servers = ceil(finalGPUs / gpusPerServer)</div>
            <div>usersPerServer = totalUsers / servers</div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
              <p className="text-sm text-muted-foreground mb-1">Total GPUs Required</p>
              <p className="text-4xl font-bold text-primary">{formatNumber(finalPlan.finalGpuCount)}</p>
            </div>
            <div className="border-2 border-primary rounded-lg p-4 bg-primary/5">
              <p className="text-sm text-muted-foreground mb-1">Servers Needed</p>
              <p className="text-4xl font-bold text-primary">{formatNumber(finalPlan.finalServerCount)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {finalPlan.gpusPerServer} GPUs per server
              </p>
            </div>
          </div>

          <div className="border border-border rounded-lg p-4">
            <p className="text-sm font-medium mb-2">Capacity Summary</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Concurrent Users:</span>
                <span className="font-medium">{formatNumber(finalPlan.inputConcurrentUsers)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Users per Server:</span>
                <span className="font-medium">{formatDecimal(finalPlan.inputConcurrentUsers / finalPlan.finalServerCount, 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Weighted Avg Tokens/Conversation:</span>
                <span className="font-medium">{formatNumber(requests.aggregateTotalTokensPerConversation)}</span>
              </div>
            </div>
          </div>

          {/* Persona duty cycle summary */}
          {personaTimings && personaTimings.length > 0 && (
            <div className="border border-border rounded-lg p-4">
              <p className="text-sm font-medium mb-3">Persona Duty Cycle Summary</p>
              <div className="grid md:grid-cols-2 gap-3">
                {personaTimings.map((timing) => (
                  <div key={timing.id} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{timing.name}:</span>
                    <span className="font-medium">
                      {formatDecimal(timing.dutyCycleBreakdown.queryPct, 0)}% active 
                      <span className="text-muted-foreground ml-1">({formatDecimal(timing.cycleSec, 1)}s cycle)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
